import React, { useEffect, useState, useRef } from "react";
import { checkIfStuck } from "../../../utils";
import { useGlobalValue } from "../../../state/GlobalProvider";
import styles from "./Header.module.sass";
import { SovosPageLayout, SovosToolbar, SovosToolbarActions, SovosMenuItem, SovosToolbarDropdown, SovosSplitButton, SovosButton, SovosCircularProgress } from "mosaic-react";
import SovosMonthSelector from "../YearSelector/index.js";
import { regexURLMatcher } from "../../../utils/routingUtils.js";
import { Download } from "mosaic-react-icons";
const renderActions = (reportsAction, downloadableQueries, download, downloading, locale, pushDialog, i18n, totalLinesCsv, buttonConfigs) => {
  const actions = [];
  const {
    showButton = false,
    type,
    label = "",
    items = []
  } = buttonConfigs;
  if (showButton && reportsAction && downloadableQueries?.length) {
    /**
     * Possible types
     * 1 -> single (Sovos Button)
     * 2 -> multiple (Sovos Split Button)
     * (...)
     */
    switch (type) {
      case "multiple":
        const menuItems = items.reduce((acc, item, index) => {
          const {
            menuLabel = ""
          } = item || {};
          acc.push( /*#__PURE__*/React.createElement(SovosMenuItem, {
            key: `download_query_${index}`,
            className: `${styles["action-button"]} ${downloading ? styles["has-activity"] : ""}`,
            onClick: () => download && download([downloadableQueries[index]], locale)
          }, menuLabel));
          return acc;
        }, []);
        actions.push( /*#__PURE__*/React.createElement(SovosSplitButton, {
          key: "download",
          onClick: () => {},
          menuItems: menuItems
        }, downloading ? /*#__PURE__*/React.createElement(SovosCircularProgress, {
          thickness: 10,
          size: "1rem"
        }) : label));
        break;
      case "upload":
        break;
      case "pushDialog":
        actions.push( /*#__PURE__*/React.createElement(SovosButton, {
          key: "pushDialog",
          className: `${styles["action-button"]}`,
          startIcon: !downloading && /*#__PURE__*/React.createElement(Download, null),
          onClick: () => {
            !downloading && pushDialog("MultipleDownloadDialog", i18n`Please choose one of the following options`, {
              queries: downloadableQueries,
              download,
              locale,
              totalLinesCsv
            }).then(() => {}).catch(() => null);
          }
        }, downloading ? /*#__PURE__*/React.createElement(SovosCircularProgress, {
          thickness: 10,
          size: "1rem"
        }) : label));
        break;
      default:
        actions.push( /*#__PURE__*/React.createElement(SovosButton, {
          key: "defaultButton",
          className: `${styles["action-button"]}`,
          onClick: () => download && download(downloadableQueries, locale),
          startIcon: !downloading && /*#__PURE__*/React.createElement(Download, null)
        }, downloading ? /*#__PURE__*/React.createElement(SovosCircularProgress, {
          thickness: 10,
          size: "1rem"
        }) : label));
        break;
    }
  }
  return actions;
};
const Header = props => {
  const {
    title,
    fiscalYears,
    importIds,
    downloadableQueries = [],
    downloading,
    reportsAction,
    totalLinesCsv,
    headerButtonComponents = [],
    buttonConfigs = {}
  } = props;
  const {
    i18n,
    locale,
    pushDialog,
    history,
    download
  } = useGlobalValue();
  const [stuck, setStuck] = useState(false);
  const elem = useRef(null);
  const handleScroll = () => {
    const isStuck = checkIfStuck(elem.current, "top");
    if (isStuck !== stuck) {
      setStuck(isStuck);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  });
  const [arrayImportIds, setArrayImportIds] = useState([]);
  const [currentFile, setCurrentFile] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [currentFiscalYears, setCurrentFiscalYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  useEffect(() => {
    if (title) {
      const finalTtile = Array.isArray(title) ? title.join(" ") : title;
      setCurrentTitle(finalTtile);
    }
  }, [title]);
  useEffect(() => {
    if (Array.isArray(importIds)) {
      setArrayImportIds(importIds);
    }
  }, [importIds]);
  useEffect(() => {
    if (fiscalYears && typeof fiscalYears === "object") {
      const yearsArray = Object.keys(fiscalYears).map(year => parseInt(year));
      setCurrentFiscalYears(yearsArray);
    }
  }, [fiscalYears]);
  useEffect(() => {
    if (history && history.location) {
      const myRegex = /^(\/(pt|en|lt|es|ro|fr)\/\d+\/)(\d{4}|current)\/?(\d+|full)?(\/*.*)/;
      const match = regexURLMatcher(myRegex, history?.location?.pathname || "");
      if (match) {
        const group4 = match[4];
        setCurrentFile(group4);
        const group3 = match[3];
        setSelectedYear(+group3);
      }
    }
  }, [JSON.stringify(history)]);
  const barActions = buttonConfigs?.showButton && downloadableQueries && downloadableQueries.length ? renderActions(reportsAction, downloadableQueries, download, downloading, locale, pushDialog, i18n, totalLinesCsv, buttonConfigs) : [];
  const actions = headerButtonComponents.length === 0 && barActions.length === 0 ? null : [...barActions, ...headerButtonComponents];
  return /*#__PURE__*/React.createElement("div", {
    key: "mainDiv",
    className: `${styles["margin-header"]}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${styles["header-root"]}`
  }, /*#__PURE__*/React.createElement(SovosPageLayout, {
    className: `${styles["sovos-page-layout"]}`,
    style: {
      height: "auto"
    },
    pageTitle: currentTitle,
    titleBarActions: actions,
    children: ""
  }), Array.isArray(importIds) && /*#__PURE__*/React.createElement("div", {
    className: `${styles["toolbar-container"]}`
  }, /*#__PURE__*/React.createElement(SovosMonthSelector, {
    className: `${styles["sovos-month-selector"]}`,
    format: "number",
    selectedYear: selectedYear,
    nextMonthTooltipText: "Next",
    previousMonthTooltipText: "Previous",
    years: currentFiscalYears,
    minDate: new Date(`${currentFiscalYears[0]}-01-01`),
    maxDate: new Date(`${currentFiscalYears[currentFiscalYears.length - 1]}-12-31`),
    onChange: e => {
      const newYear = e.year;
      const currentData = fiscalYears[newYear];
      let lastImportId;
      if (Array.isArray(currentData)) {
        let fiscalYearIds = currentData.map(item => item.id);
        lastImportId = fiscalYearIds.indexOf("full") !== -1 ? "full" : Math.max(...fiscalYearIds);
      }
      const myRegex = /^(\/(pt|en|lt|es|ro|fr)\/\d+\/)(\d{4}|current)\/?(\d+|full)?(\/*.*)/;
      const match = regexURLMatcher(myRegex, history.location.pathname);
      if (match && match[4]) {
        history.push(history.location.pathname.replace(myRegex, `$1${newYear}/${lastImportId}$5`));
      } else {
        history.push(history.location.pathname.replace(myRegex, `$1${newYear}/$5`));
      }
    }
  }), /*#__PURE__*/React.createElement(SovosToolbar, {
    className: `${styles["sovos-toolbar"]}`
  }, /*#__PURE__*/React.createElement(SovosToolbarActions, {
    className: `${styles["toolbar-actions"]}`
  }, /*#__PURE__*/React.createElement(SovosToolbarDropdown, {
    className: `${styles["toolbar-dropdown"]}`,
    value: currentFile,
    onChange: event => {
      setCurrentFile(event.target.value);
      const myRegex = /^(\/(pt|en|lt|es|ro|fr)\/\d+\/)(\d{4}|current)\/?(\d+|full)?(\/*.*)/;
      history.push(history.location.pathname.replace(myRegex, `$1$3/${event.target.value}$5`));
    }
  }, arrayImportIds.map(year => /*#__PURE__*/React.createElement(SovosMenuItem, {
    key: year.id,
    value: year.id
  }, year.id === "full" ? year.name : `${year.id} - ${year.name}`))))))));
};
export default Header;